import { DEFAULT, SHORT, NUMERIC, VALID_FORMATS } from './consts';
import { isValidDate, getValidLocale, isNumber } from './helpers';

/**
 * `displayDate` takes a `Date` object and a format specifier, and returns a string representing date that is formatting according to format for the user's locale.
 * @param date - `Date` object or `number` to display as `string`.
 * @param options - parameters object available for the Intl.DateTimeFormat() constructor:
 *   * `format` - either "default", "short", or "numeric".
 *   * `locale` - developer designated locale (this will take precedence over browser setting, if language differs).
 *   * `timeZone` - converts `date` to the given timezone.
 *   * `utc` - converts `date` to UTC timezone if true. Defaults to false.
 * See MDN docs for complete list of options: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#options
 * @returns a locale date string corresponding to current user's locale and format options.
 */
export const displayDate = function (date) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (!isValidDate(date)) {
    throw new TypeError(`The first argument must be a valid date but was "${date}".`);
  }
  const {
    format,
    locale,
    utc,
    timeZone,
    weekday,
    year
  } = options;
  if (!VALID_FORMATS.has(format)) {
    const supported = Array.from(VALID_FORMATS).filter(f => !!f).map(f => JSON.stringify(f));
    supported[2] = `or ${supported[2]}`;
    throw new TypeError(`The format must be either: ${supported.join(', ')} but was "${format}".`);
  }
  const _options = {};
  _options.day = NUMERIC;
  _options.year = NUMERIC;
  switch (format) {
    case NUMERIC:
      _options.month = NUMERIC;
      break;
    case SHORT:
    case DEFAULT:
    default:
      _options.month = SHORT;
      if (weekday) {
        _options.weekday = SHORT;
      }
      if (year === false) {
        delete _options.year;
      }
      break;
  }
  _options.timeZone = timeZone || (utc ? 'UTC' : undefined);
  const localeId = getValidLocale(locale);
  const finalDate = new Date(isNumber(date) ? date : date.getTime());
  return finalDate.toLocaleDateString(localeId, _options);
};
//# sourceMappingURL=displayDate.js.map